import enums from '@/helpers/enums';
import { cloneDeep, isEmpty } from 'lodash';

export const state = () => ({
  all: {},
  categories: [],
  design_groups: [],
  designers: [],
  blog_authors: [],
  content_authors: [],
  asset_contributors: [],
  products: [],
  industries: [],
  topics: [],
  catalog_content_pages: [],
  content_admins: []
});

export const getters = {
  all: (state) => {
    return state.all || {};
  },
  template_design_types: (state, getters) => {
    return getters.all.template_design_types || [];
  },
  template_design_type_name: (state, getters) => (id) => {
    return getters.template_design_types.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  contributor_payout_methods: (state, getters) => {
    return getters.all.contributor_payout_methods || [];
  },
  roles: (state, getters) => {
    return getters.all.roles || [];
  },
  category_types: (state, getters) => {
    return getters.all.category_types || [];
  },
  units: (state, getters) => {
    return getters.all.units || [];
  },
  folds: (state, getters) => {
    return getters.all.folds || [];
  },
  template_guide_types: (state, getters) => {
    return getters.all.template_guide_types || [];
  },
  optimization_profiles: (state, getters) => {
    return getters.all.optimization_profiles || [];
  },
  impositions: (state, getters) => {
    return getters.all.impositions || [];
  },
  print_providers: (state, getters) => {
    return getters.all.print_providers || [];
  },
  print_provider_name: (state, getters) => (id) => {
    return getters.print_providers.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  campaign_types: (state, getters) => {
    return getters.all.campaign_types || [];
  },
  campaign_type_name: (state, getters) => (id) => {
    return getters.campaign_types.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  industry_types: (state, getters) => {
    return getters.all.industry_types || [];
  },
  order_line_statuses: (state, getters) => {
    return getters.all.order_line_statuses || [];
  },
  countries(state, getters) {
    return getters.all.countries || [];
  },
  states(state, getters) {
    return (countryId) => {
      return (getters.all.states || []).filter((x) => x.countryId === countryId) || [];
    };
  },
  blog_post_types(state, getters) {
    return getters.all.blog_post_types || [];
  },
  blog_post_type_name: (state, getters) => (id) => {
    return getters.blog_post_types.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  pages(state, getters) {
    return getters.all.pages || [];
  },
  popularity(state, getters) {
    return getters.all.popularity || [];
  },
  review_statuses(state, getters) {
    return getters.all.review_statuses || [];
  },
  review_status_name: (state, getters) => (id) => {
    return getters.review_statuses.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  product_types(state, getters) {
    return getters.all.product_types || [];
  },
  dashboard_url(state, getters) {
    return getters.all.dashboard_url || '';
  },
  product_type_name: (state, getters) => (id) => {
    return getters.product_types.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  mfg_types(state, getters) {
    return getters.all.mfg_types || [];
  },
  mfg_type_name: (state, getters) => (id) => {
    return getters.mfg_types.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  master_accounts(state, getters) {
    return getters.all.master_accounts || [];
  },
  master_account_name: (state, getters) => (id) => {
    return getters.master_accounts.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  cache_keys(state, getters) {
    return getters.all.cache_keys || [];
  },
  validation_types(state, getters) {
    return getters.all.validation_types || [];
  },
  content_page_types(state, getters) {
    return getters.all.content_page_types || [];
  },
  category_groups(state, getters) {
    return getters.all.category_groups || [];
  },
  industry_groups(state, getters) {
    return getters.all.industry_groups || [];
  },

  categories: (state) => {
    return state.categories || [];
  },
  parent_categories: (state, getters) => {
    return getters.categories.filter((x) => !x.parentCategoryId);
  },
  non_parent_categories: (state, getters) => {
    const list = getters.categories;

    const parentIds = list.filter((x) => x.parentCategoryId).map((x) => x.parentCategoryId);

    return list.filter((x) => !parentIds.includes(x.id));
  },
  design_groups: (state) => {
    return state.design_groups || [];
  },
  designGroupsByIndustryOrTopic: (state, getters) => (industryId, topicId) => {
    if (topicId) {
      return getters.design_groups.filter((x) => x.topicId === topicId);
    }
    return getters.design_groups.filter((x) => x.industryId === industryId);
  },
  design_group_name: (state, getters) => (id) => {
    return getters.design_groups.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  design_group_non_admin: (state, getters) => {
    return getters.design_groups.filter((x) => !x.is_admin_group && x.active);
  },
  designers: (state) => {
    return state.designers || [];
  },
  blog_authors: (state) => {
    return state.blog_authors || [];
  },
  content_authors: (state) => {
    return state.content_authors || [];
  },
  asset_contributors: (state) => {
    return state.asset_contributors || [];
  },
  products: (state) => {
    return state.products || [];
  },
  product_name: (state, getters) => (id) => {
    return getters.products.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  product_name_with_orientation: (state, getters) => (id) => {
    return getters.products.filter((x) => x.id === id).map((x) => x.nameWithOrientation)[0];
  },
  standard_products: (state, getters) => {
    return getters.products.filter((x) => x.units.id !== enums.UNITS.MILLIMETERS);
  },
  productsByCategory: (state, getters) => (categoryId) => {
    return getters.standard_products.filter((x) => x.category.id === categoryId);
  },
  metric_products: (state, getters) => {
    return getters.products.filter((x) => x.units.id === enums.UNITS.MILLIMETERS);
  },
  similar_metric_products: (state, getters) => (categoryId) => {
    return getters.metric_products.filter((x) => x.active && x.category.id === categoryId);
  },
  category_name: (state, getters) => (id) => {
    return getters.categories.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  industries: (state) => {
    return state.industries || [];
  },
  industry_name: (state, getters) => (id) => {
    return getters.industries.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  // Every topic, retired included. Use this for FILTERS and for naming existing records — a retired topic still
  // has to be reachable, not least because you can't retire one until its published templates are moved and
  // finding those means filtering to it. For anything that offers a topic to build on, use activeTopics.
  topics: (state) => {
    return state.topics || [];
  },
  // Topics you're still allowed to attach new work to. `active` already accounts for the parent industry.
  activeTopics: (state, getters) => {
    return getters.topics.filter((x) => x.active);
  },
  topic_name: (state, getters) => (id) => {
    return getters.topics.filter((x) => x.id === id).map((x) => x.name)[0];
  },
  // Pick-list oriented: every consumer of this is choosing where to put something new.
  topicsByIndustry: (state, getters) => (industryId) => {
    return getters.activeTopics.filter((x) => x.industryId === industryId);
  },
  catalog_content_pages: (state) => {
    return state.catalog_content_pages || [];
  },
  catalog_content_page_name: (state, getters) => (id) => {
    return getters.catalog_content_pages.filter((x) => x.id === id).map((x) => x.pageName)[0];
  },
  numbers: () => (min, max) => {
    var numbers = [];
    for (var i = min; i <= max; i++) {
      numbers.push({ name: i, value: i });
    }
    return numbers;
  },
  content_admins: (state) => {
    return state.content_admins || [];
  }
};

export const mutations = {
  SET_LOOKUPS(state, lookups) {
    state.all = lookups;
  },
  SET_CATEGORIES(state, list) {
    state.categories = list;
  },
  SET_DESIGN_GROUPS(state, list) {
    state.design_groups = list;
  },
  ADD_DESIGN_GROUP(state, item) {
    state.design_groups.push({ id: item.id, name: item.nameHierarchy });
  },
  SET_DESIGNERS(state, list) {
    state.designers = list;
  },
  SET_BLOG_AUTHORS(state, list) {
    state.blog_authors = list;
  },
  SET_CONTENT_AUTHORS(state, list) {
    state.content_authors = list;
  },
  SET_ASSET_CONTRIBUTORS(state, list) {
    state.asset_contributors = list;
  },
  SET_PRODUCTS(state, list) {
    state.products = list;
  },
  SET_PRODUCT(state, item) {
    const index = state.products.findIndex((x) => x.id === item.id);
    if (index >= 0) {
      this._vm.$set(state.products, index, item);
    } else {
      state.products.push(item);
    }
  },
  SET_INDUSTRIES(state, list) {
    state.industries = list;
  },
  ADD_INDUSTRY(state, item) {
    state.industries.push(item);
  },
  SET_TOPICS(state, list) {
    state.topics = list;
  },
  ADD_TOPIC(state, item) {
    state.topics.push(item);
  },
  ADD_CATEGORY(state, item) {
    state.categories.push(item);
  },
  SET_CATALOG_CONTENT_PAGES(state, list) {
    state.catalog_content_pages = list;
  },
  SET_CONTENT_ADMINS(state, list) {
    state.content_admins = list;
  },
  SET_PAGE_TYPE(state, item) {
    const index = state.all.content_page_types.findIndex((x) => x.id === item.id);
    if (index < 0) {
      state.all.content_page_types.push(cloneDeep(item));
    } else {
      this._vm.$set(state.all.content_page_types, index, cloneDeep(item));
    }
  }
};

export const actions = {
  async get({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.all)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getAll')
        .then((response) => {
          commit('SET_LOOKUPS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getCategories({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.categories)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getCategoriesSelect')
        .then((response) => {
          commit('SET_CATEGORIES', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getDesignGroups({ commit, state }, force) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.design_groups) && !force) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getDesignGroupsSelect')
        .then((response) => {
          commit('SET_DESIGN_GROUPS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getDesigners({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.designers)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getDesignersSelect')
        .then((response) => {
          commit('SET_DESIGNERS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getBlogAuthors({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.blog_authors)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getBlogAuthorsSelect')
        .then((response) => {
          commit('SET_BLOG_AUTHORS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // The authors a blog post can be attributed to. Sourced from AuthorInfo (what the byline is actually
  // resolved from) rather than the BlogAuthor role that getBlogAuthors uses — the two sets are not the same.
  async getContentAuthors({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.content_authors)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getContentAuthorsSelect')
        .then((response) => {
          commit('SET_CONTENT_AUTHORS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getAssetContributors({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.asset_contributors)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getAssetContributorsSelect')
        .then((response) => {
          commit('SET_ASSET_CONTRIBUTORS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getProducts({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.products)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getProducts')
        .then((response) => {
          commit('SET_PRODUCTS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getIndustries({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.industries)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getIndustriesSelect')
        .then((response) => {
          commit('SET_INDUSTRIES', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getTopics({ commit, state }, force) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.topics) && !force) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getTopicsSelect')
        .then((response) => {
          commit('SET_TOPICS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getCatalogContentPages({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.catalog_content_pages)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getCatalogContentPagesSelect')
        .then((response) => {
          commit('SET_CATALOG_CONTENT_PAGES', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  async getContentAdmins({ commit, state }) {
    return new Promise((resolve, reject) => {
      if (!isEmpty(state.content_admins)) {
        resolve();
        return;
      }
      this._vm.$http
        .get('lookups/getContentAdminsSelect')
        .then((response) => {
          commit('SET_CONTENT_ADMINS', response.data);
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  },
  // eslint-disable-next-line no-empty-pattern
  async syncCache({}, keys) {
    return new Promise((resolve, reject) => {
      this._vm.$http
        .post('lookups/syncCache', keys)
        .then(() => {
          resolve();
        })
        .catch((error) => {
          reject(error);
        });
    });
  }
};

export default {
  namespaced: true,
  state,
  getters,
  actions,
  mutations
};
